import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, Phone, Users } from "lucide-react";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_DISPLAY = "+39 377 366 0476";
const PHONE_WA = "393773660476";
const COMMUNITY_URL = "https://chat.whatsapp.com/BYtWElK92VpIFAHOAd1H4X";
const EMAIL = "metododimare@gmail.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => {
    const val = k === "consent" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: val });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) return;
    if (!form.consent) {
      toast.error("Devi accettare l'informativa privacy per inviare il messaggio.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        message: form.message,
        consent: true,
      });
      toast.success("Messaggio inviato. Ti risponderemo presto.");
      setSent(true);
      setForm({ name: "", email: "", message: "", consent: false });
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o scrivi su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 border-t border-[#E5E1DA] bg-[#0B192C] text-[#F9F8F6]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-5"
          >
            <p className="overline text-[#E89B6C]">Contatti</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Parliamo
              <br />
              <em className="text-[#E89B6C]">a quattr'occhi.</em>
            </h2>
            <p className="mt-6 text-lg font-light text-[#F9F8F6]/80 max-w-md">
              Scrivimi per informazioni sul libro, eventi, collaborazioni o
              percorsi formativi. Rispondo personalmente.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 w-10 h-10 border border-[#F9F8F6]/30 flex items-center justify-center group-hover:border-[#D4B895] transition-colors">
                  <Mail size={16} strokeWidth={1.4} />
                </span>
                <div>
                  <p className="overline text-[#E89B6C]">Email</p>
                  <p className="font-serif-display text-xl mt-1 group-hover:text-[#E89B6C] transition-colors">
                    {EMAIL}
                  </p>
                </div>
              </a>

              <a
                href={`https://wa.me/${PHONE_WA}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 w-10 h-10 bg-[#25D366] flex items-center justify-center">
                  <WhatsAppIcon size={18} className="text-white" />
                </span>
                <div>
                  <p className="overline text-[#E89B6C]">WhatsApp</p>
                  <p className="font-serif-display text-xl mt-1 group-hover:text-[#E89B6C] transition-colors">
                    {PHONE_DISPLAY}
                  </p>
                </div>
              </a>

              <a
                href={COMMUNITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-community"
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 w-10 h-10 bg-[#D4B895] flex items-center justify-center">
                  <Users size={16} strokeWidth={1.4} className="text-[#0B192C]" />
                </span>
                <div>
                  <p className="overline text-[#E89B6C]">Community</p>
                  <p className="font-serif-display text-xl mt-1 group-hover:text-[#E89B6C] transition-colors">
                    Entra nel gruppo Metodo di Mare
                  </p>
                </div>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="md:col-span-6 md:col-start-7"
          >
            {!sent ? (
              <form onSubmit={submit} className="space-y-6">
                <div>
                  <label className="overline text-[#E89B6C] block mb-2">Nome</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Il tuo nome"
                    data-testid="contact-name-input"
                    className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-lg font-light text-[#F9F8F6] placeholder:text-[#F9F8F6]/30 focus:outline-none focus:border-[#D4B895]"
                  />
                </div>
                <div>
                  <label className="overline text-[#E89B6C] block mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="nome@esempio.com"
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-lg font-light text-[#F9F8F6] placeholder:text-[#F9F8F6]/30 focus:outline-none focus:border-[#D4B895]"
                  />
                </div>
                <div>
                  <label className="overline text-[#E89B6C] block mb-2">Messaggio</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Raccontami cosa cerchi..."
                    data-testid="contact-message-input"
                    className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-lg font-light text-[#F9F8F6] placeholder:text-[#F9F8F6]/30 focus:outline-none focus:border-[#D4B895] resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-[#D4B895] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      Invia messaggio
                      <ArrowRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
                <p className="text-xs uppercase tracking-[0.18em] text-[#F9F8F6]/50">
                  I tuoi dati arrivano direttamente a {EMAIL}
                </p>
              </form>
            ) : (
              <div className="border border-[#D4B895] p-10">
                <p className="overline text-[#E89B6C]">Messaggio ricevuto</p>
                <p className="font-serif-display text-3xl mt-3">
                  Grazie, ti risponderò personalmente.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
